import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { CareGrid } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { slicerParam, sortParam, type SlicerDef } from '@lib/grid';
import { fmtMemberId } from '@lib/member-id';
import { listArchivedEvents } from '@lib/care-api';
import { EventDrawer } from '@components/EventDrawer';
import { openEvent } from '@lib/open-mode';
import { ArchiveTabs } from '@components/ArchiveTabs';
import { NotByCampaignNote } from '@components/CampaignScopeNote';
import {
  ARCHIVE_ENTRIES_WORDING as W,
  CHANNEL_META,
  CLOSED_VIA_WORDING,
  SOURCE_META,
  STATUS_META,
  emptyNotice,
  emptyRow,
  emptyTone,
  fmtTs,
  statusLabel,
  type ArchivedEventRow,
  type ArchivedSortKey,
  type EmptyReason,
  type EventStatus,
} from '@lib/care-model';

/**
 * Archive (closed entries), nav 08's second view (William's word, R. 82, 19 Sep 2026: "Move the
 * closed files to an Archive table in the db for Care").
 *
 * Since the archive agent moves every closed entry out of the queue's folder on every cycle, the
 * Inbound queue shows open entries only. The closed ones live in member_care.db, which the builder
 * refreshes every ten minutes, and THIS screen reads them from there through
 * GET /api/care/archived-events: one page at a time, so search, the status slicer, the date range
 * and the sort all go to the server, which answers over the whole table (the row 356 rule for a
 * paged screen). Read-only: a row opens the event drawer in its archived mode.
 */

/** The first non-empty line of the preview, capped, as the Summary cell. */
export function summaryOf(preview: string, cap = 120): string {
  const line = (preview || '').split('\n').map((s) => s.trim()).find(Boolean) ?? '';
  return line.length > cap ? `${line.slice(0, cap)}...` : line;
}

/** The Closed by / when cell: the closer as recorded, or plain words when the record holds none. */
export function closerWords(r: Pick<ArchivedEventRow, 'closedBy' | 'closedTs' | 'closedVia'>): string {
  if (r.closedVia === 'none' || (!r.closedBy && !r.closedTs)) return W.notRecorded;
  const who = r.closedBy || 'Unnamed';
  const when = r.closedTs ? fmtTs(r.closedTs) : '';
  const tail = r.closedVia === 'last-action' ? ' (last action)' : '';
  return `${who}${when ? ` · ${when}` : ''}${tail}`;
}

/**
 * A date the operator picks becomes an instant on HER clock: the whole local day, opened at
 * 00:00:00 and closed at 23:59:59.999, so "Sep 1" means her Sep 1 and not the UTC one.
 */
export function localDayBounds(from: string, to: string): { from?: string; to?: string } {
  const out: { from?: string; to?: string } = {};
  if (from) {
    const d = new Date(`${from}T00:00:00`);
    if (!Number.isNaN(d.getTime())) out.from = d.toISOString();
  }
  if (to) {
    const d = new Date(`${to}T23:59:59.999`);
    if (!Number.isNaN(d.getTime())) out.to = d.toISOString();
  }
  return out;
}

const SORT_KEYS: readonly ArchivedSortKey[] = ['received', 'member', 'status', 'channel', 'source', 'closed'];

const SLICERS: SlicerDef<ArchivedEventRow>[] = [
  { key: 'status', label: 'Status', value: () => null, labelFor: (v) => statusLabel(v as EventStatus) },
];

function columns(): GridColumn<ArchivedEventRow>[] {
  return [
    { key: 'received', label: 'Received', firstDir: 'desc', tdStyle: { whiteSpace: 'nowrap' }, render: (r) => fmtTs(r.receivedTs) },
    {
      key: 'channel', label: 'Channel',
      render: (r) => {
        const c = CHANNEL_META[r.channel] ?? CHANNEL_META.Unknown;
        return <span className={`pill ${c.pill}`.trim()}>{c.label}</span>;
      },
    },
    {
      key: 'source', label: 'Source',
      render: (r) => {
        const s = SOURCE_META[r.source] ?? SOURCE_META.member;
        return <span className={`pill ${s.pill}`.trim()}>{s.label}</span>;
      },
    },
    { key: 'member', label: 'Member', render: (r) => r.memberName || <span style={{ color: 'var(--ink-3)' }}>Unmatched</span> },
    { key: 'id', label: 'ID', tdClassName: 'mono', render: (r) => (r.memberId ? fmtMemberId(r.memberId) : '-') },
    {
      key: 'status', label: 'Status',
      render: (r) => {
        const st = STATUS_META[r.status] ?? { label: r.status, pill: '' };
        return <span className={`pill ${st.pill}`.trim()}>{st.label}</span>;
      },
    },
    {
      key: 'closed', label: 'Closed by / when', firstDir: 'desc', title: 'Who closed the entry and when, as the record carries it',
      tdStyle: { whiteSpace: 'nowrap' },
      render: (r) => (
        <span title={CLOSED_VIA_WORDING[r.closedVia]?.hint} style={r.closedVia === 'none' ? { color: 'var(--ink-3)' } : undefined}>
          {closerWords(r)}
        </span>
      ),
    },
    { key: 'summary', label: 'Summary', render: (r) => summaryOf(r.preview) || <span style={{ color: 'var(--ink-3)' }}>{r.subject || '-'}</span> },
  ];
}

export function ArchiveEntries(): JSX.Element {
  const [items, setItems] = useState<ArchivedEventRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState<EmptyReason | undefined>(undefined);
  const [dbAsOf, setDbAsOf] = useState<string | null>(null);
  const [facets, setFacets] = useState<Record<string, Array<{ v: string; n: number }>>>({});
  const [sliceHidden, setSliceHidden] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  // The search box: typed into `draft`, sent as `q` on Enter / Search, so the server is not asked
  // once per keystroke over a table of thousands.
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');

  const view = useGridView('archive-entries', columns(), {
    defaultSort: [{ k: 'received', asc: false }],
    slicers: SLICERS,
    serverSortable: SORT_KEYS,
  });
  const sortWire = sortParam(view.sort);
  const sliceWire = slicerParam(view.slicers);
  const bounds = localDayBounds(fromDay, toDay);
  const fromIso = bounds.from ?? '';
  const toIso = bounds.to ?? '';

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    listArchivedEvents({
      q, sort: sortWire, ...(sliceWire ? { slice: sliceWire } : {}), ...(fromIso ? { from: fromIso } : {}), ...(toIso ? { to: toIso } : {}),
    })
      .then((r) => {
        setItems(r.items);
        setTotal(r.total);
        setFacets(r.facets ?? {});
        setSliceHidden(r.sliceHidden ?? 0);
        setReason(r.emptyReason);
        setDbAsOf(r.dbAsOf);
        setOpenId(null); // a re-fetch can reshuffle; never leave the drawer open on a stale id
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load the closed entries.'))
      .finally(() => setLoading(false));
  }, [q, sortWire, sliceWire, fromIso, toIso]);

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    setError('');
    listArchivedEvents({
      q, offset: items.length, sort: sortWire, ...(sliceWire ? { slice: sliceWire } : {}), ...(fromIso ? { from: fromIso } : {}), ...(toIso ? { to: toIso } : {}),
    })
      .then((r) => {
        setTotal(r.total);
        setDbAsOf(r.dbAsOf);
        // Dedupe by eventId: an entry archived between two pages shifts the window by one.
        setItems((prev) => {
          const seen = new Set(prev.map((x) => x.eventId));
          return [...prev, ...r.items.filter((x) => !seen.has(x.eventId))];
        });
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load more entries.'))
      .finally(() => setLoadingMore(false));
  }, [q, items.length, sortWire, sliceWire, fromIso, toIso]);

  useEffect(() => {
    load();
  }, [load]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    setQ(draft.trim());
  };

  const more = Math.max(total - items.length, 0);
  const unreachable = reason === 'source-unreachable';

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">Member Care &middot; History</div>
        <h1 className="section-title">{W.title}</h1>
        <div className="section-rule" />
        <p className="section-lede">
          {W.lede}{' '}
          {total ? `${total.toLocaleString()} closed entr${total === 1 ? 'y' : 'ies'}${more ? `; showing the first ${items.length}` : ''}.` : ''}
        </p>
      </header>
      <ArchiveTabs active="entries" />
      <div className="note" role="note" data-testid="archive-entries-note" style={{ marginBottom: 12 }}>
        {W.note}
        {dbAsOf ? <span className="muted"> {W.fresh(fmtTs(dbAsOf))}</span> : null}
      </div>
      {/* Row 354: the rows carry a campaign, but this screen is paged on the server and the campaign
          menu is not sent to it, so a campaign selection is SAID not to apply here. */}
      <NotByCampaignNote screen="The closed entries" />

      <form onSubmit={submitSearch} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }} role="search">
        <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
          Refresh
        </button>
        <label className="small" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          From
          <input className="text-input" type="date" aria-label="Received from" value={fromDay} max={toDay || undefined} onChange={(e) => setFromDay(e.target.value)} />
        </label>
        <label className="small" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          To
          <input className="text-input" type="date" aria-label="Received to" value={toDay} min={fromDay || undefined} onChange={(e) => setToDay(e.target.value)} />
        </label>
        {(fromDay || toDay) && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFromDay(''); setToDay(''); }}>
            Any date
          </button>
        )}
        <input
          className="text-input"
          type="search"
          aria-label="Search the closed entries"
          style={{ marginLeft: 'auto', maxWidth: 340 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={W.searchPlaceholder}
        />
        <button type="submit" className="btn btn-sm">Search</button>
        {q && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setDraft(''); setQ(''); }}>
            Clear
          </button>
        )}
      </form>

      {error && <div className="note danger" style={{ marginBottom: 14 }}>{error}</div>}
      {!loading && reason && reason !== 'no-rows' && (
        <div className={`note ${emptyTone(reason)}`} role="status" style={{ marginBottom: 14 }}>
          {emptyNotice(reason, W.unreadable)}
        </div>
      )}

      <CareGrid
        view={view}
        rows={items}
        rowKey={(r) => r.eventId}
        noun="entries"
        server={{ facets, hidden: sliceHidden }}
        rowProps={(r) => ({
          onClick: () => openEvent(r.eventId, setOpenId),
          style: { cursor: 'pointer' },
          'data-testid': `archived-row-${r.eventId}`,
        })}
        empty={
          loading ? 'Loading closed entries...'
            : unreachable ? emptyRow(reason, W.unreadable)
              : q || fromDay || toDay ? W.noRows : W.nothingYet
        }
      />

      {!loading && more > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
          <span style={{ color: 'var(--ink-3)', fontSize: '0.88em' }}>
            Showing {items.length} of {total.toLocaleString()} closed entries.
          </span>
        </div>
      )}

      {openId && (
        <EventDrawer
          eventId={openId}
          archived
          onClose={() => setOpenId(null)}
          onChanged={() => undefined}
        />
      )}
    </div>
  );
}
